import { getDynamicQuestionCount } from '../../config/SiteConfigManager';
import { isMobileDevice } from '../device/mobileDetection';

export interface DynamicQuestionItem {
    id: string;
    text: string;
    slotIndex: number;
}

export function getDynamicQuestionDisplayCount(domain: string): number {
    if (isMobileDevice()) {
        return 0;
    }

    const configuredCount = getDynamicQuestionCount(domain);

    if (configuredCount !== undefined && configuredCount !== null) {
        return configuredCount;
    }

    return 2;
}

export function buildDynamicQuestionItems(
    questions: string[],
    count: number,
    options: {
        rotationOffset?: number;
        shuffledIndices?: number[] | null;
    } = {}
): DynamicQuestionItem[] {
    if (count <= 0 || questions.length === 0) {
        return [];
    }

    const { rotationOffset, shuffledIndices } = options;
    const questionsToShow = Math.min(count, questions.length);
    const selectedItems: DynamicQuestionItem[] = [];
    const usedIndices = new Set<number>();

    for (let slotIndex = 0; slotIndex < questionsToShow; slotIndex++) {
        let selectedIndex: number;

        if (shuffledIndices && shuffledIndices.length > 0) {
            const shuffledArrayIndex = ((rotationOffset ?? 0) + slotIndex) % shuffledIndices.length;
            selectedIndex = shuffledIndices[shuffledArrayIndex] ?? 0;
        } else if (rotationOffset !== undefined) {
            selectedIndex = (rotationOffset + slotIndex) % questions.length;
        } else {
            do {
                selectedIndex = Math.floor(Math.random() * questions.length);
            } while (usedIndices.has(selectedIndex) && usedIndices.size < questions.length);
        }

        usedIndices.add(selectedIndex);
        const selectedQuestion = questions[selectedIndex];
        if (!selectedQuestion) {
            continue;
        }
        selectedItems.push({
            id: `dynamic-question-${slotIndex + 1}`,
            text: selectedQuestion,
            slotIndex,
        });
    }

    return selectedItems;
}

export function createDynamicQuestionRotationTimer(
    domain: string,
    callback: () => void,
    options: {
        initialDelaySeconds?: number;
        rotationSeconds?: number;
        shouldContinue?: () => boolean;
    } = {}
): () => void {
    if (getDynamicQuestionDisplayCount(domain) === 0) {
        return () => {};
    }

    const { initialDelaySeconds = 3, rotationSeconds = 12, shouldContinue = () => true } = options;
    const initialDelay = initialDelaySeconds * 1000;
    const rotationInterval = rotationSeconds * 1000;

    let recurringTimer: NodeJS.Timeout | null = null;

    const initialTimer = setTimeout(() => {
        if (!shouldContinue()) {
            return;
        }

        callback();

        recurringTimer = setInterval(() => {
            if (!shouldContinue()) {
                if (recurringTimer) {
                    clearInterval(recurringTimer);
                    recurringTimer = null;
                }
                return;
            }

            callback();
        }, rotationInterval);
    }, initialDelay);

    return () => {
        clearTimeout(initialTimer);
        if (recurringTimer) {
            clearInterval(recurringTimer);
        }
    };
}
